import { type MouseEvent, useCallback, useEffect, useState } from 'react';

/** Marker attribute for text regions that should stay mouse-selectable. */
export const SELECTABLE_TEXT_ATTR = 'data-sticky-text';

/**
 * Native HTML5 drag turns a `draggable` element's WHOLE subtree into a drag
 * source, so a mouse press on its text starts a drag instead of a selection —
 * the title/body can't be selected or copied. This hook keeps the element
 * draggable everywhere EXCEPT while a press begins on a text region marked with
 * `SELECTABLE_TEXT_ATTR`: there it momentarily turns `draggable` off so the
 * browser performs a native text selection, then restores it once the press
 * ends. Dragging from the grip handle, chrome, padding, or whitespace is
 * unaffected, so drag-and-drop (move + stack) keeps working.
 *
 * `draggable` is React state (not a raw DOM mutation) so a re-render can't
 * silently restore it mid-selection; mousedown is a discrete event, so the flip
 * is flushed to the DOM before the browser decides drag-vs-select on the first
 * mousemove.
 */
export function useTextSelectableDrag() {
  const [selecting, setSelecting] = useState(false);

  // Idempotent: every press re-evaluates against its target, so a press off
  // text always CLEARS the flag too — not just sets it. That way a state left
  // stuck by a selection released off the card auto-recovers on the next press,
  // and the card drags on the first try.
  const onMouseDown = useCallback((e: MouseEvent) => {
    setSelecting((e.target as HTMLElement).closest(`[${SELECTABLE_TEXT_ATTR}]`) != null);
  }, []);

  // A selection gesture commonly ends OUTSIDE the card (drag past its border to
  // extend the selection), where the element's own mouseup never fires. Listen
  // on the document while selecting so the release resets drag wherever it
  // lands; detached as soon as the flag clears.
  useEffect(() => {
    if (!selecting) return;
    const end = () => setSelecting(false);
    document.addEventListener('mouseup', end);
    return () => document.removeEventListener('mouseup', end);
  }, [selecting]);

  return {
    /** Feed to the draggable element's `draggable` prop. */
    draggable: !selecting,
    onMouseDown,
    /** Spread onto each text region that should stay mouse-selectable. */
    textProps: { [SELECTABLE_TEXT_ATTR]: '' } as Record<string, string>,
  };
}
